import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Bar, Brush, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { fmtMoney } from '../../../Documents/format';
import { CHART } from '../../constants';
import { axisLabel, fmtCompact, fmtInt, pointLabel, signed } from '../../format';
import { Granularity, MovementPoint, MovementTimeline } from '../../types';
import { AXIS_TICK, BORDER, NoData, SeriesLegend, SeriesToggle, Stat, StatStrip, TipBox, TooltipInjected, tooltipPoint } from './chartKit';

const SERIES: SeriesToggle[] = [
  { key: 'createdSum', label: 'Внесено', color: CHART.created },
  { key: 'updatePlusSum', label: 'Правки: увеличение', color: CHART.updatePlus },
  { key: 'updateMinusSum', label: 'Правки: уменьшение', color: CHART.updateMinus },
  { key: 'deletedSum', label: 'Удалено', color: CHART.deleted },
  { key: 'balanceSum', label: 'Остаток журнала (правая ось)', color: CHART.balance, line: true }
];

const BRUSH_FROM = 45;
const money = (v: number) => fmtMoney(v);

const MovementTip: React.FC<TooltipInjected<MovementPoint> & { granularity?: Granularity }> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  return (
    <TipBox
      width={290}
      title={pointLabel(p.key, props.granularity || 'day')}
      subtitle={`${fmtInt(p.actions)} действий · аптек: ${fmtInt(p.pharmacies)}`}
      rows={[
        { label: `Внесено (${fmtInt(p.createdCount)} зап.)`, value: signed(p.createdSum, money) || fmtMoney(0), color: CHART.created },
        { label: 'Правки: увеличение', value: signed(p.updatePlusSum, money) || fmtMoney(0), color: CHART.updatePlus },
        { label: 'Правки: уменьшение', value: signed(p.updateMinusSum, money) || fmtMoney(0), color: CHART.updateMinus, hint: p.updatedCount ? `правок всего: ${fmtInt(p.updatedCount)}` : undefined },
        { label: `Удалено (${fmtInt(p.deletedCount)} зап.)`, value: signed(p.deletedSum, money) || fmtMoney(0), color: CHART.deleted },
        { label: 'Изменение за период', value: signed(p.netSum, money) || fmtMoney(0), bold: true, divider: true, hint: p.netQty ? `${signed(p.netQty, fmtInt)} уп.` : undefined },
        { label: 'Остаток журнала на конец', value: fmtMoney(p.balanceSum), color: CHART.balance, bold: true, hint: `${fmtInt(p.balanceQty)} уп.` }
      ]}
    />
  );
};

interface Props {
  data: MovementTimeline;
  height?: number;
  /** Полоса итогов над графиком */
  stats?: boolean;
}

/** Движение журнала по истории: внесено / правки / удалено столбиками со знаком и остаток журнала линией */
const MovementChart: React.FC<Props> = ({ data, height = 320, stats = true }) => {
  const [hidden, setHidden] = useState<string[]>([]);
  const toggle = useCallback((key: string) => setHidden(h => (h.indexOf(key) !== -1 ? h.filter(k => k !== key) : h.concat(key))), []);
  const show = (key: string) => hidden.indexOf(key) === -1;
  const points = data.points;

  const totals = useMemo(() => points.reduce((acc, p) => ({
    created: acc.created + p.createdSum,
    createdCount: acc.createdCount + p.createdCount,
    plus: acc.plus + p.updatePlusSum,
    minus: acc.minus + p.updateMinusSum,
    updated: acc.updated + p.updatedCount,
    deleted: acc.deleted + p.deletedSum,
    deletedCount: acc.deletedCount + p.deletedCount,
    net: acc.net + p.netSum,
    actions: acc.actions + p.actions
  }), { created: 0, createdCount: 0, plus: 0, minus: 0, updated: 0, deleted: 0, deletedCount: 0, net: 0, actions: 0 }), [points]);

  if (!points.length) return <NoData height={height} text="По выбранным фильтрам изменений в истории нет" />;

  const last = points[points.length - 1];
  const items: Stat[] = [
    { label: 'Остаток на начало', value: fmtMoney(data.opening.sum), hint: `${fmtInt(data.opening.qty)} уп.`, tooltip: 'Сумма журнала до первого дня периода по истории изменений' },
    { label: 'Внесено', value: fmtMoney(totals.created), hint: `${fmtInt(totals.createdCount)} зап.`, color: CHART.created },
    { label: 'Правки: увеличение', value: signed(totals.plus, money) || fmtMoney(0), hint: `правок: ${fmtInt(totals.updated)}`, color: CHART.updatePlus },
    { label: 'Правки: уменьшение', value: signed(totals.minus, money) || fmtMoney(0), color: '#b45309' },
    { label: 'Удалено', value: signed(totals.deleted, money) || fmtMoney(0), hint: `${fmtInt(totals.deletedCount)} зап.`, color: CHART.deleted },
    { label: 'Изменение за период', value: signed(totals.net, money) || fmtMoney(0), hint: `действий: ${fmtInt(totals.actions)}` },
    { label: 'Остаток на конец', value: fmtMoney(last.balanceSum), hint: `${fmtInt(last.balanceQty)} уп.`, color: CHART.balance }
  ];

  return (
    <Box>
      {stats && <StatStrip items={items} />}
      <SeriesLegend items={SERIES} hidden={hidden} onToggle={toggle} />
      <Box sx={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} stackOffset="sign" margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis dataKey="key" tick={AXIS_TICK} tickFormatter={k => axisLabel(String(k), data.granularity)} axisLine={{ stroke: BORDER }} tickLine={false} minTickGap={16} />
            <YAxis yAxisId="left" tick={AXIS_TICK} tickFormatter={v => fmtCompact(Number(v))} axisLine={false} tickLine={false} width={64} />
            <YAxis yAxisId="right" orientation="right" tick={AXIS_TICK} tickFormatter={v => fmtCompact(Number(v))} axisLine={false} tickLine={false} width={64} hide={!show('balanceSum')} />
            <ReferenceLine yAxisId="left" y={0} stroke="#cbd5e1" />
            <ChartTooltip content={<MovementTip granularity={data.granularity} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
            {show('createdSum') && <Bar yAxisId="left" dataKey="createdSum" stackId="m" fill={CHART.created} isAnimationActive={false} maxBarSize={36} />}
            {show('updatePlusSum') && <Bar yAxisId="left" dataKey="updatePlusSum" stackId="m" fill={CHART.updatePlus} isAnimationActive={false} maxBarSize={36} />}
            {show('updateMinusSum') && <Bar yAxisId="left" dataKey="updateMinusSum" stackId="m" fill={CHART.updateMinus} isAnimationActive={false} maxBarSize={36} />}
            {show('deletedSum') && <Bar yAxisId="left" dataKey="deletedSum" stackId="m" fill={CHART.deleted} isAnimationActive={false} maxBarSize={36} />}
            {show('balanceSum') && (
              <Line yAxisId="right" dataKey="balanceSum" type="monotone" stroke={CHART.balance} strokeWidth={2} dot={false} isAnimationActive={false} />
            )}
            {points.length > BRUSH_FROM && (
              <Brush
                key={points.length} dataKey="key" height={22} stroke="#94a3b8" travellerWidth={8}
                startIndex={Math.max(0, points.length - BRUSH_FROM)} tickFormatter={k => axisLabel(String(k), data.granularity)}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(MovementChart);
